// Showing a game table

import { useFirebase } from './FirebaseContextProvider';
import { timeStr } from '../utils/misc';
import { gameDivision } from '../utils/gameReport';
import placeholderTeam from '../assets/images/placeholder-team.png';
import './GameListTable.scss';

export default function GameListTable({
  games,
  fields,
  colorLegend,
  noFieldCol,
  noDivisionCol,
  noGameCol,
  noScoreCol,
  noRefereeCol,
  renderRate,
}) {
  const { teamsById, coachesByTeamId, refereesById } = useFirebase();

  // Keeps a team or coach name unbroken on one line
  const nbsp = (name) => name.replace(/ /g, ' ');

  const renderTeam = (team, teamId) => {
    // A coach invited but not yet through their profile carries no name — left
    // in, they would render as a stray separator, and nbsp would throw on them
    const coachNames = (coachesByTeamId.get(teamId) || [])
      .map((coach) => coach.name)
      .filter(Boolean);

    return (
      <div className='team'>
        {team && (
          <div
            className='bg-image wh-32'
            style={{
              backgroundImage: `url(${team.imgThumbUrl || placeholderTeam})`,
            }}
          />
        )}
        <div className='team-info'>
          <span className='font-medium'>
            {team?.name ? nbsp(team.name) : 'TBD'}
          </span>
          {coachNames.length > 0 && (
            <span className='coach'>{coachNames.map(nbsp).join(', ')}</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className='game-list-table'>
      <div className='scroll-area'>
        <div className='header'>
          <h6 className='col-1'>Time</h6>
          {!noFieldCol && <h6 className='col-6'>Field</h6>}
          {!noDivisionCol && <h6 className='col-3'>Division</h6>}
          {!noGameCol && <h6 className='col-4'>Game</h6>}
          {!noScoreCol && <h6 className='col-2'>Score</h6>}
          {!noRefereeCol && <h6 className='col-5'>Referees</h6>}
          {renderRate && <h6 className='col-7'>Rate</h6>}
        </div>

        <div className='lines'>
          {games.map((game) => {
            const { id, start, duration, field, score1, score2 } = game;

            const time = timeStr(start, duration);

            const fieldName = fields.find((f) => f.id === field)?.name || '';

            const team1 = teamsById.get(game.team1);
            const team2 = teamsById.get(game.team2);

            const division = gameDivision(game, teamsById);

            const bgColor = `${colorLegend[division]?.color || '000000'}30`;

            return (
              <div key={id} className='line'>
                <p
                  className='col-1'
                  style={{
                    backgroundColor: bgColor,
                  }}
                >
                  {time}
                </p>
                {!noFieldCol && (
                  <p
                    style={{
                      backgroundColor: bgColor,
                    }}
                    className='col-6 '
                  >
                    {fieldName}
                  </p>
                )}
                {!noDivisionCol && (
                  <p
                    style={{
                      backgroundColor: bgColor,
                    }}
                    className='col-3 '
                  >
                    {division}
                  </p>
                )}
                {!noGameCol && (
                  <div
                    style={{
                      backgroundColor: bgColor,
                    }}
                    className='col-4 game-cell'
                  >
                    {renderTeam(team1, game.team1)}
                    <span className='dash'>-</span>
                    {renderTeam(team2, game.team2)}
                  </div>
                )}
                {!noScoreCol && (
                  <p
                    style={{
                      backgroundColor: bgColor,
                    }}
                    className='col-2  font-medium'
                  >
                    {score1 !== null || score2 !== null
                      ? `${score1 === null ? '' : score1} : ${
                          score2 === null ? '' : score2
                        }`
                      : '-'}
                  </p>
                )}
                {!noRefereeCol && (
                  <div
                    style={{
                      backgroundColor: bgColor,
                    }}
                    className='col-5 referees-cell'
                  >
                    {(game.referees || []).map((refereeId) => (
                      // Not nbsp'd like team names: the column is narrow, so a
                      // long name has to wrap instead of overflowing the cell
                      <span key={refereeId}>
                        {refereesById.get(refereeId)?.name || ''}
                      </span>
                    ))}
                  </div>
                )}
                {renderRate && (
                  <div
                    style={{
                      backgroundColor: bgColor,
                    }}
                    className='col-7 rate-cell'
                  >
                    {renderRate(game)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
